package chand.wordvoyage

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import wordvoyage.shared.generated.resources.Res
import wordvoyage.shared.generated.resources.ic_volume_up
import wordvoyage.shared.generated.resources.ic_book
import wordvoyage.shared.generated.resources.ic_usage
import wordvoyage.shared.generated.resources.ic_examples
import chand.wordvoyage.data.WordEntry
import chand.wordvoyage.platform.rememberTextToSpeech
import chand.wordvoyage.ui.FancyDetailCard
import chand.wordvoyage.ui.FancyLinkCard
import chand.wordvoyage.ui.SectionColorExamples
import chand.wordvoyage.ui.SectionColorMeaning
import chand.wordvoyage.ui.SectionColorPronunciation
import chand.wordvoyage.ui.SectionColorUsage
import chand.wordvoyage.ui.WordHeroBanner
import chand.wordvoyage.ui.WordVoyageTopBar
import chand.wordvoyage.ui.AdBanner

@Composable
fun WordHistoryDetailScreen(
    entry: WordEntry,
    onBack: () -> Unit
) {
    val tts = rememberTextToSpeech()

    Scaffold(
        topBar = { WordVoyageTopBar(title = "", onBack = onBack) },
        bottomBar = { AdBanner() },
        containerColor = MaterialTheme.colorScheme.background
    ) { innerPadding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .verticalScroll(rememberScrollState())
        ) {
            // ── Hero banner ──────────────────────────────────────────────
            WordHeroBanner(
                word = entry.word,
                subtitle = entry.howToPronounce.ifBlank { "" },
                accentColor = SectionColorMeaning
            )

            Column(
                modifier = Modifier.padding(horizontal = 16.dp, vertical = 20.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                // Pronunciation card
                if (entry.howToPronounce.isNotBlank()) {
                    FancyDetailCard(
                        title = "How to Pronounce",
                        icon = Res.drawable.ic_volume_up,
                        accentColor = SectionColorPronunciation,
                        onSpeak = { tts.speak(entry.word) }
                    ) {
                        Text(
                            text = entry.howToPronounce,
                            style = MaterialTheme.typography.bodyLarge,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                }

                // Meaning card
                FancyDetailCard(
                    title = "Meaning",
                    icon = Res.drawable.ic_book,
                    accentColor = SectionColorMeaning
                ) {
                    Text(
                        text = entry.meaning,
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                }

                // Usage card
                if (entry.wordUsage.isNotBlank()) {
                    FancyDetailCard(
                        title = "How to Use",
                        icon = Res.drawable.ic_usage,
                        accentColor = SectionColorUsage
                    ) {
                        Text(
                            text = entry.wordUsage,
                            style = MaterialTheme.typography.bodyLarge,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                }

                // Examples card
                if (entry.examplesOfUsingWord.isNotEmpty()) {
                    FancyDetailCard(
                        title = "Examples",
                        icon = Res.drawable.ic_examples,
                        accentColor = SectionColorExamples
                    ) {
                        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            entry.examplesOfUsingWord.forEachIndexed { index, example ->
                                Row(modifier = Modifier.fillMaxWidth()) {
                                    Text(
                                        text = "${index + 1}.",
                                        style = MaterialTheme.typography.bodyLarge,
                                        fontWeight = FontWeight.Bold,
                                        color = SectionColorExamples,
                                        modifier = Modifier.padding(end = 8.dp)
                                    )
                                    Text(
                                        text = example,
                                        style = MaterialTheme.typography.bodyLarge,
                                        color = MaterialTheme.colorScheme.onSurface,
                                        modifier = Modifier.weight(1f)
                                    )
                                }
                            }
                        }
                    }
                }

                // Learn More link card
                if (entry.websiteLinkToStillMoreInfo.isNotBlank()) {
                    val uriHandler = LocalUriHandler.current
                    FancyLinkCard(url = entry.websiteLinkToStillMoreInfo) {
                        try { uriHandler.openUri(entry.websiteLinkToStillMoreInfo) }
                        catch (e: Exception) { println("URL error: ${e.message}") }
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))
            }
        }
    }
}
